import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaTrophy,
  FaBullseye,
  FaUsers,
  FaTrendingUp,
  FaChartBar,
  FaRunning,
  FaCalendarAlt,
  FaAward,
  FaBolt,
  FaShieldAlt,
  FaClock,
  FaMapMarkerAlt,
  FaStar,
  FaFire,
  FaMedal,
  FaChartLine,
  FaEye,
  FaFilter
} from 'react-icons/fa';
import { fetchEquipesChampionnat, fetchStatsEquipeChampionnat } from '../api/fff';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import Navbar from '../components/Layout/Navbar';
import TopBar from '../components/Layout/TopBar';
import APIDebugPanel from '../components/Layout/APIDebugPanel';

const Stats = () => {
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('equipes');
  const [equipes, setEquipes] = useState([]);
  const [statsEquipes, setStatsEquipes] = useState({});
  const [statsGenerales, setStatsGenerales] = useState({
    totalMatchs: 0,
    totalButsMarques: 0,
    totalButsEncaisses: 0,
    totalVictoires: 0,
    totalNuls: 0,
    totalDefaites: 0,
    meilleurButeur: null,
    totalJoueurs: 0,
    moyenneAge: 0
  });
  const [statsJoueurs, setStatsJoueurs] = useState([]);
  const [activeEquipe, setActiveEquipe] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadAllStats();
  }, []);

  const loadAllStats = async () => {
    try {
      setLoading(true);

      // Charger les équipes
      const equipesData = await fetchEquipesChampionnat();
      setEquipes(equipesData);

      // Charger les stats de chaque équipe
      const statsEquipesTemp = {};
      let statsLoadedCount = 0;

      for (const equipe of equipesData) {
        try {
          // On utilise directement les IDs récupérés par fetchEquipesChampionnat
          if (equipe.cp_no && equipe.ph_no && equipe.gp_no) {
            const stats = await fetchStatsEquipeChampionnat({
              cpNo: equipe.cp_no,
              phNo: equipe.ph_no,
              gpNo: equipe.gp_no,
              nomEquipe: equipe.nom
            });

            if (stats) {
              statsEquipesTemp[equipe.fffId] = {
                ...stats,
                equipeNom: equipe.nom,
                championnat: equipe.championnat
              };
              statsLoadedCount++;
            }
          }
        } catch (error) {
          console.error(`Erreur stats ${equipe.nom}:`, error);
        }
      }

      setStatsEquipes(statsEquipesTemp);

      // Charger les stats des joueurs depuis Firestore
      let joueursData = [];
      try {
        const joueursSnap = await getDocs(collection(db, 'joueurs'));
        joueursData = joueursSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(j => j.visible !== false)
          .sort((a, b) => (b.buts || 0) - (a.buts || 0));
        setStatsJoueurs(joueursData);
      } catch (error) {
        console.error('Erreur chargement joueurs Firestore:', error);
      }

      // Calculer les stats générales
      calculateGeneralStats(statsEquipesTemp, joueursData);

      if (statsLoadedCount === 0 && equipesData.length > 0) {
        console.warn('⚠️ Aucune statistique d\'équipe n\'a pu être chargée depuis l\'API FFF');
      }

    } catch (error) {
      console.error('Erreur critique loadAllStats:', error);
      // Initialiser avec des valeurs par défaut pour éviter une page vide
      setStatsGenerales({
        totalMatchs: 0,
        totalButsMarques: 0,
        totalButsEncaisses: 0,
        totalVictoires: 0,
        totalNuls: 0,
        totalDefaites: 0,
        meilleurButeur: null,
        totalJoueurs: 0,
        moyenneAge: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateGeneralStats = (statsEquipes, joueurs) => {
    const equipeStats = Object.values(statsEquipes);

    const generalStats = {
      totalMatchs: equipeStats.reduce((sum, s) => sum + (s.total_games_count || 0), 0),
      totalButsMarques: equipeStats.reduce((sum, s) => sum + (s.goals_for_count || 0), 0),
      totalButsEncaisses: equipeStats.reduce((sum, s) => sum + (s.goals_against_count || 0), 0),
      totalVictoires: equipeStats.reduce((sum, s) => sum + (s.won_games_count || 0), 0),
      totalNuls: equipeStats.reduce((sum, s) => sum + (s.draw_games_count || 0), 0),
      totalDefaites: equipeStats.reduce((sum, s) => sum + (s.lost_games_count || 0), 0),
      meilleurButeur: joueurs[0] || null,
      totalJoueurs: joueurs.length,
      moyenneAge: joueurs.length > 0 ?
        Math.round(joueurs.reduce((sum, j) => sum + (j.age || 25), 0) / joueurs.length) : 0
    };

    setStatsGenerales(generalStats);
  };

  const getEquipePerformance = (stats) => {
    const total = stats.total_games_count || 0;
    if (total === 0) return 0;
    return Math.round(((stats.won_games_count || 0) * 3 + (stats.draw_games_count || 0)) / (total * 3) * 100);
  };

  const tabs = [
    { id: 'equipes', label: 'Nos Équipes', icon: FaUsers, description: 'Performance de toutes nos équipes' },
    { id: 'joueurs', label: 'Nos Joueurs', icon: FaBullseye, description: 'Top buteurs et passeurs' },
    { id: 'general', label: 'Vue Globale', icon: FaChartBar, description: 'Statistiques générales du club' },
    { id: 'records', label: 'Records', icon: FaTrophy, description: 'Nos meilleurs records' }
  ];

  const globalStats = [
    {
      label: 'Matchs joués',
      value: statsGenerales.totalMatchs || 0,
      icon: FaRunning,
      color: 'text-[#FFE400]',
      bgColor: 'from-[#FFE400]/20 to-[#BDFF00]/20'
    },
    {
      label: 'Buts marqués',
      value: statsGenerales.totalButsMarques || 0,
      icon: FaBullseye,
      color: 'text-[#BDFF00]',
      bgColor: 'from-[#BDFF00]/20 to-[#FFE400]/20'
    },
    {
      label: 'Équipes actives',
      value: equipes.length || 0,
      icon: FaUsers,
      color: 'text-[#FFE400]',
      bgColor: 'from-[#FFE400]/20 to-[#BDFF00]/20'
    },
    {
      label: 'Joueurs licenciés',
      value: statsGenerales.totalJoueurs || 0,
      icon: FaFire,
      color: 'text-[#BDFF00]',
      bgColor: 'from-[#BDFF00]/20 to-[#FFE400]/20'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#001B56] via-[#002a7a] to-[#001B56]">
        <TopBar />
        <div className="sticky top-4 z-50 flex flex-col items-center w-full pt-10">
          <div className="relative w-[90vw] max-w-2xl">
            <Navbar />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-16">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="inline-block rounded-full h-16 w-16 border-4 border-[#FFE400] border-t-transparent mx-auto mb-4"
            />
            <p className="text-xl text-blue-200">Chargement des statistiques...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#001B56] via-[#002a7a] to-[#001B56]">
      <TopBar />
      <div className="sticky top-4 z-50 flex flex-col items-center w-full pt-10">
        <div className="relative w-[90vw] max-w-2xl">
          <Navbar />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="backdrop-blur-md bg-white/5 rounded-2xl p-8 border border-white/10 relative overflow-hidden">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-10 left-10 text-8xl">📊</div>
              <div className="absolute bottom-10 right-10 text-8xl">🏆</div>
              <div className="absolute top-1/2 left-1/4 text-6xl">⚽</div>
            </div>

            <div className="relative z-10 text-center">
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
                className="flex items-center justify-center gap-4 mb-6"
              >
                <div className="p-4 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-3xl shadow-lg">
                  <FaChartLine className="text-4xl text-[#001B56]" />
                </div>
                <h1 className="text-4xl md:text-6xl font-bold text-white">
                  STATISTIQUES EFAFC
                </h1>
              </motion.div>

              <p className="text-xl text-blue-200 mb-8 max-w-3xl mx-auto">
                Toutes les statistiques de nos équipes et joueurs en temps réel 📈
              </p>

              {/* Stats globales rapides */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                {globalStats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className={`backdrop-blur-sm bg-gradient-to-br ${stat.bgColor} rounded-2xl p-4 border border-white/30 hover:scale-105 transition-transform duration-300`}
                  >
                    <div className="flex items-center justify-center mb-2">
                      <stat.icon className={`text-2xl ${stat.color}`} />
                    </div>
                    <div className="text-2xl font-bold text-white">{stat.value}</div>
                    <div className="text-sm text-blue-200">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Navigation par onglets */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="backdrop-blur-md bg-white/5 rounded-xl p-2 border border-white/10">
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <motion.button
                    key={tab.id}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedTab(tab.id)}
                    className={`flex items-center gap-3 px-6 py-3 rounded-xl transition-all duration-300 ${selectedTab === tab.id
                      ? 'bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] shadow-lg'
                      : 'text-white hover:bg-white/20'
                      }`}
                  >
                    <IconComponent className="text-lg" />
                    <div className="text-left hidden sm:block">
                      <div className="font-medium">{tab.label}</div>
                      <div className="text-xs opacity-80">{tab.description}</div>
                    </div>
                    <div className="sm:hidden font-medium">{tab.label}</div>
                  </motion.button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Contenu des onglets */}
        <AnimatePresence mode="wait">
          {selectedTab === 'equipes' && (
            <motion.div
              key="equipes"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="backdrop-blur-md bg-white/5 rounded-2xl p-8 border border-white/10">
                <div className="flex items-center gap-4 mb-8">
                  <div className="p-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-xl">
                    <FaUsers className="text-2xl text-[#001B56]" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">Performance de nos Équipes</h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {Object.entries(statsEquipes).map(([equipeId, stats], index) => (
                    <motion.div
                      key={equipeId}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -5 }}
                      className="backdrop-blur-md bg-white/5 rounded-2xl p-6 border border-white/10 hover:border-white/20 hover:bg-white/8 transition-all duration-300"
                    >
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h3 className="text-xl font-bold text-white">{stats.championnat}</h3>
                          <p className="text-blue-300/70 text-sm">{stats.equipeNom}</p>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <FaMedal className="text-[#FFE400]" />
                            <span className="text-2xl font-bold text-[#FFE400]">
                              {stats.rank}<sup>e</sup>
                            </span>
                          </div>
                          <div className="text-sm text-blue-300/70">{stats.point_count} pts</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-3 mb-6">
                        <div className="text-center bg-[#BDFF00]/10 border border-[#BDFF00]/20 rounded-xl p-3">
                          <div className="text-lg font-bold text-[#BDFF00]">{stats.won_games_count}</div>
                          <div className="text-xs text-[#BDFF00]/70">Victoires</div>
                        </div>
                        <div className="text-center bg-[#FFE400]/10 border border-[#FFE400]/20 rounded-xl p-3">
                          <div className="text-lg font-bold text-[#FFE400]">{stats.draw_games_count}</div>
                          <div className="text-xs text-[#FFE400]/70">Nuls</div>
                        </div>
                        <div className="text-center bg-rose-500/10 border border-rose-500/20 rounded-xl p-3">
                          <div className="text-lg font-bold text-rose-400">{stats.lost_games_count}</div>
                          <div className="text-xs text-rose-400/70">Défaites</div>
                        </div>
                      </div>

                      <div className="flex justify-between items-center text-sm mb-2">
                        <span className="text-blue-300/70">Performance</span>
                        <span className="font-bold text-[#BDFF00]">{getEquipePerformance(stats)}%</span>
                      </div>
                      <div className="w-full bg-white/5 rounded-full h-2.5 border border-white/10">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${getEquipePerformance(stats)}%` }}
                          transition={{ duration: 1, delay: index * 0.2 }}
                          className="bg-gradient-to-r from-[#FFE400] to-[#BDFF00] h-2.5 rounded-full"
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-white/10">
                        <div className="text-center">
                          <div className="text-lg font-bold text-[#BDFF00]">{stats.goals_for_count}</div>
                          <div className="text-xs text-blue-300/60">Buts +</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-rose-400">{stats.goals_against_count}</div>
                          <div className="text-xs text-blue-300/60">Buts -</div>
                        </div>
                        <div className="text-center">
                          <div className={`text-lg font-bold ${stats.goals_diff >= 0 ? 'text-[#BDFF00]' : 'text-rose-400'}`}>
                            {stats.goals_diff > 0 ? '+' : ''}{stats.goals_diff}
                          </div>
                          <div className="text-xs text-blue-300/60">Diff</div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {selectedTab === 'joueurs' && (
            <motion.div
              key="joueurs"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              {/* Top buteurs */}
              <div className="backdrop-blur-md bg-white/5 rounded-2xl p-8 border border-white/10">
                <div className="flex items-center gap-4 mb-8">
                  <div className="p-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-xl">
                    <FaBullseye className="text-2xl text-[#001B56]" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">Top Buteurs</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {statsJoueurs.slice(0, 9).map((joueur, index) => (
                    <motion.div
                      key={joueur.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ scale: 1.02, y: -2 }}
                      className="backdrop-blur-md bg-white/5 rounded-xl p-4 border border-white/10 hover:border-white/20 hover:bg-white/8 transition-all duration-300"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${index === 0 ? 'bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56]' :
                          index === 1 ? 'bg-white/20 text-white border border-white/30' :
                            index === 2 ? 'bg-[#FFE400]/20 text-[#FFE400] border border-[#FFE400]/30' :
                              'bg-white/10 text-white/70 border border-white/10'
                          }`}>
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="font-bold text-white">{joueur.prenom} {joueur.nom}</div>
                          <div className="text-xs text-blue-300/60">{joueur.poste}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-[#BDFF00]">{joueur.buts || 0}</div>
                          <div className="text-xs text-blue-300/60">buts</div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Top passeurs */}
              <div className="backdrop-blur-md bg-white/5 rounded-2xl p-8 border border-white/10">
                <div className="flex items-center gap-4 mb-8">
                  <div className="p-3 bg-gradient-to-r from-[#BDFF00] to-[#FFE400] rounded-xl">
                    <FaBolt className="text-2xl text-[#001B56]" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">Top Passeurs</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {statsJoueurs
                    .sort((a, b) => (b.passes || 0) - (a.passes || 0))
                    .slice(0, 9)
                    .map((joueur, index) => (
                      <motion.div
                        key={joueur.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.02, y: -2 }}
                        className="backdrop-blur-md bg-white/5 rounded-xl p-4 border border-white/10 hover:border-white/20 hover:bg-white/8 transition-all duration-300"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${index === 0 ? 'bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56]' :
                            index === 1 ? 'bg-white/20 text-white border border-white/30' :
                              index === 2 ? 'bg-[#FFE400]/20 text-[#FFE400] border border-[#FFE400]/30' :
                                'bg-white/10 text-white/70 border border-white/10'
                            }`}>
                            {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                          </div>
                          <div className="flex-1">
                            <div className="font-bold text-white">{joueur.prenom} {joueur.nom}</div>
                            <div className="text-xs text-blue-300/60">{joueur.poste}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xl font-bold text-[#FFE400]">{joueur.passes || 0}</div>
                            <div className="text-xs text-blue-300/60">passes</div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                </div>
              </div>
            </motion.div>
          )}

          {selectedTab === 'general' && (
            <motion.div
              key="general"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="backdrop-blur-md bg-white/5 rounded-2xl p-8 border border-white/10"
            >
              <div className="flex items-center gap-4 mb-8">
                <div className="p-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-xl">
                  <FaChartBar className="text-2xl text-[#001B56]" />
                </div>
                <h2 className="text-2xl font-bold text-white">Vue d'Ensemble du Club</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="backdrop-blur-md bg-[#BDFF00]/5 rounded-xl p-6 text-center border border-[#BDFF00]/20"
                >
                  <FaRunning className="text-4xl text-[#BDFF00] mx-auto mb-4" />
                  <div className="text-3xl font-bold text-white mb-2">
                    {Math.round((statsGenerales.totalVictoires / Math.max(statsGenerales.totalMatchs, 1)) * 100)}%
                  </div>
                  <div className="text-[#BDFF00]/80 mb-4 font-medium">Taux de victoire global</div>
                  <div className="text-sm text-blue-300/60">
                    {statsGenerales.totalVictoires} victoires sur {statsGenerales.totalMatchs} matchs
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="backdrop-blur-md bg-white/5 rounded-xl p-6 text-center border border-white/10 hover:border-white/20 transition-all duration-300"
                >
                  <FaBullseye className="text-4xl text-[#FFE400] mx-auto mb-4" />
                  <div className="text-3xl font-bold text-white mb-2">
                    {(statsGenerales.totalButsMarques / Math.max(statsGenerales.totalMatchs, 1)).toFixed(1)}
                  </div>
                  <div className="text-[#FFE400]/80 mb-4 font-medium">Moyenne buts/match</div>
                  <div className="text-sm text-blue-300/60">
                    {statsGenerales.totalButsMarques} buts marqués
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="backdrop-blur-md bg-white/5 rounded-xl p-6 text-center border border-white/10 hover:border-white/20 transition-all duration-300"
                >
                  <FaShieldAlt className="text-4xl text-rose-400 mx-auto mb-4" />
                  <div className="text-3xl font-bold text-white mb-2">
                    {(statsGenerales.totalButsEncaisses / Math.max(statsGenerales.totalMatchs, 1)).toFixed(1)}
                  </div>
                  <div className="text-rose-400/80 mb-4 font-medium">Moyenne buts encaissés</div>
                  <div className="text-sm text-blue-300/60">
                    {statsGenerales.totalButsEncaisses} buts encaissés
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="backdrop-blur-md bg-white/5 rounded-xl p-6 text-center border border-white/10 hover:border-white/20 transition-all duration-300"
                >
                  <FaUsers className="text-4xl text-[#BDFF00] mx-auto mb-4" />
                  <div className="text-3xl font-bold text-white mb-2">
                    {statsGenerales.moyenneAge}
                  </div>
                  <div className="text-[#BDFF00]/80 mb-4 font-medium">Âge moyen</div>
                  <div className="text-sm text-blue-300/60">
                    Sur {statsGenerales.totalJoueurs} joueurs licenciés
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="backdrop-blur-md bg-[#FFE400]/5 rounded-xl p-6 text-center border border-[#FFE400]/20"
                >
                  <FaTrophy className="text-4xl text-[#FFE400] mx-auto mb-4" />
                  <div className="text-3xl font-bold text-white mb-2">
                    {statsGenerales.meilleurButeur?.buts || 0}
                  </div>
                  <div className="text-[#FFE400]/80 mb-4 font-medium">Meilleur buteur</div>
                  <div className="text-sm text-blue-300/60">
                    {statsGenerales.meilleurButeur ?
                      `${statsGenerales.meilleurButeur.prenom} ${statsGenerales.meilleurButeur.nom}` :
                      'Aucun buteur enregistré'
                    }
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="backdrop-blur-md bg-white/5 rounded-xl p-6 text-center border border-white/10 hover:border-white/20 transition-all duration-300"
                >
                  <FaCalendarAlt className="text-4xl text-[#FFE400] mx-auto mb-4" />
                  <div className="text-3xl font-bold text-white mb-2">
                    {new Date().getFullYear() - 1920}
                  </div>
                  <div className="text-[#FFE400]/80 mb-4 font-medium">Années d'existence</div>
                  <div className="text-sm text-blue-300/60">
                    Club fondé en 1920
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}

          {selectedTab === 'records' && (
            <motion.div
              key="records"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <div className="backdrop-blur-md bg-white/5 rounded-2xl p-8 border border-white/10">
                <div className="flex items-center gap-4 mb-8">
                  <div className="p-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-xl">
                    <FaTrophy className="text-2xl text-[#001B56]" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">Nos Records</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="backdrop-blur-md bg-[#FFE400]/5 rounded-xl p-6 border border-[#FFE400]/20"
                  >
                    <h3 className="text-xl font-bold text-[#FFE400] mb-6 flex items-center gap-3">
                      <FaAward className="text-2xl" />
                      Records Offensifs 🔥
                    </h3>
                    <div className="space-y-3">
                      {[
                        { label: 'Plus gros score', value: '6-1', icon: '⚽' },
                        { label: 'Plus de buts en un match', value: '6 buts', icon: '🎯' },
                        { label: 'Série de victoires', value: '5 matchs', icon: '🏃' },
                        { label: 'Invincibilité', value: '8 matchs', icon: '💪' }
                      ].map((record, index) => (
                        <motion.div
                          key={record.label}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex justify-between items-center p-3 bg-white/5 rounded-lg border border-white/10"
                        >
                          <span className="text-white/80 flex items-center gap-2">
                            <span>{record.icon}</span>
                            {record.label}
                          </span>
                          <span className="font-bold text-[#FFE400]">{record.value}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="backdrop-blur-md bg-[#BDFF00]/5 rounded-xl p-6 border border-[#BDFF00]/20"
                  >
                    <h3 className="text-xl font-bold text-[#BDFF00] mb-6 flex items-center gap-3">
                      <FaShieldAlt className="text-2xl" />
                      Records Défensifs 🛡️
                    </h3>
                    <div className="space-y-3">
                      {[
                        { label: 'Clean sheets consécutifs', value: '4 matchs', icon: '🧤' },
                        { label: 'Moins de buts encaissés', value: '0 but', icon: '🚫' },
                        { label: 'Meilleure défense saison', value: '12 buts', icon: '🔒' },
                        { label: 'Gardien le plus fiable', value: '85%', icon: '🥅' }
                      ].map((record, index) => (
                        <motion.div
                          key={record.label}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex justify-between items-center p-3 bg-white/5 rounded-lg border border-white/10"
                        >
                          <span className="text-white/80 flex items-center gap-2">
                            <span>{record.icon}</span>
                            {record.label}
                          </span>
                          <span className="font-bold text-[#BDFF00]">{record.value}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="backdrop-blur-md bg-white/5 rounded-xl p-6 border border-white/10 mt-8"
                >
                  <h3 className="text-xl font-bold text-[#FFE400] mb-6 flex items-center gap-3">
                    <FaStar className="text-2xl" />
                    Records Individuels ⭐
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      {[
                        { label: 'Meilleur buteur saison', value: '22 buts', icon: '🥅' },
                        { label: 'Plus de passes décisives', value: '15 passes', icon: '🎯' },
                        { label: 'Plus jeune buteur', value: '16 ans', icon: '👶' }
                      ].map((record, index) => (
                        <motion.div
                          key={record.label}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex justify-between items-center p-3 bg-white/5 rounded-lg border border-white/10"
                        >
                          <span className="text-white/80 flex items-center gap-2">
                            <span>{record.icon}</span>
                            {record.label}
                          </span>
                          <span className="font-bold text-[#FFE400]">{record.value}</span>
                        </motion.div>
                      ))}
                    </div>
                    <div className="space-y-3">
                      {[
                        { label: 'Plus de matchs joués', value: '156 matchs', icon: '🏃' },
                        { label: 'Capitaine le plus fidèle', value: '8 saisons', icon: '👑' },
                        { label: 'Cartons rouge en carrière', value: '2 cartons', icon: '🟥' }
                      ].map((record, index) => (
                        <motion.div
                          key={record.label}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 + 0.3 }}
                          className="flex justify-between items-center p-3 bg-white/5 rounded-lg border border-white/10"
                        >
                          <span className="text-white/80 flex items-center gap-2">
                            <span>{record.icon}</span>
                            {record.label}
                          </span>
                          <span className="font-bold text-[#FFE400]">{record.value}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Panel de diagnostic API (développement uniquement) */}
      <APIDebugPanel />
    </div>
  );
};

export default Stats;